import { useState } from "react";
import { Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { EnumProductType } from "gql/generated";
import { SimpleFlag } from "shared/components";
import {
  formatPrice,
  getMultilingualString,
  type EventSessionOption,
} from "shared/utils";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { CollapsableStep, UpsellSelectionCard } from "widgets/booker/shared";
import {
  DAY_CARD_THRESHOLD,
  useEventPurchaseFlow,
} from "./useEventPurchaseFlow";
import { EventDaySelector, formatDayLabel } from "./EventDaySelector";
import { shouldShowLastFew } from "shared/merchCatalog";

export const EventSessionPickerStep = () => {
  const { t } = useTranslation("booker", { keyPrefix: "EVENT_SESSION_PICKER" });
  const { product, language } = useBookerContext();
  const { cart, addCartItem, updateCartItem, removeCartItem } = useCartContext();
  const {
    flow,
    sessionsByDate,
    bookableSessions,
    days,
    dayCount,
    hasTimeSlots,
    availabilityBySessionId,
    isDaySoldOut,
    isToday,
    isTomorrow,
    hasNoFutureSessions,
    sellingConfig,
  } = useEventPurchaseFlow();

  const [activeDay, setActiveDay] = useState<string>("");

  if (flow !== "sessionPicker" || !product) return null;

  const productId = product._id;
  const maxTicketsPerPurchase =
    product.eventMetadata?.maxTicketsPerPurchase ?? undefined;
  const price =
    sellingConfig.basePrice ?? product.pricing?.tiers?.[0]?.price ?? 0;

  const eventCartRows = cart.filter(
    item => item.productId.split("::")[0] === productId,
  );
  const selectedCount = eventCartRows.reduce(
    (sum, item) => sum + (item.quantity ?? 0),
    0,
  );

  // A max of 1 per purchase means the buyer picks exactly one session, so each
  // card is a select toggle rather than a quantity stepper.
  const isSinglePick = maxTicketsPerPurchase === 1;
  const copyCount = bookableSessions.length === 1 || isSinglePick ? 1 : 2;

  // Day picker is needed for time-slot events, or when there are too many days
  // to lay out as cards. A single day never needs one.
  const useDayPicker =
    dayCount > 1 && (hasTimeSlots || dayCount >= DAY_CARD_THRESHOLD);

  const setSessionQuantity = (session: EventSessionOption, quantity: number) => {
    const cartKey = `${productId}::${session.sessionId}`;
    const existing = cart.find(item => item.productId === cartKey);
    if (quantity === 0) {
      removeCartItem(cartKey);
      return;
    }
    // Swap the previous pick in one update: cart mutations read a ref that only
    // refreshes on render, so a remove followed by an add would drop the remove.
    const previousPick = isSinglePick
      ? eventCartRows.find(item => item.productId !== cartKey)
      : undefined;
    if (previousPick) {
      updateCartItem(previousPick.productId, {
        productId: cartKey,
        sessionId: session.sessionId,
        startDateISO: session.dateISO,
        endDateISO: session.dateISO,
        quantity: 1,
      });
      return;
    }
    if (existing) {
      updateCartItem(cartKey, { quantity });
      return;
    }
    addCartItem({
      productId: cartKey,
      productName: getMultilingualString(product.name, language),
      productType: EnumProductType.Event,
      startDateISO: session.dateISO,
      endDateISO: session.dateISO,
      guestsPerTier: [],
      total: 0,
      quantity,
      sessionId: session.sessionId,
    });
  };

  const renderSessionCard = (session: EventSessionOption) => {
    const cartKey = `${productId}::${session.sessionId}`;
    const currentQty =
      cart.find(item => item.productId === cartKey)?.quantity ?? 0;
    const availability = availabilityBySessionId[session.sessionId];
    const isUnlimited = availability?.isUnlimited ?? true;
    const remaining = availability?.remainingCapacity ?? null;
    const globalCap =
      maxTicketsPerPurchase != null
        ? currentQty + Math.max(maxTicketsPerPurchase - selectedCount, 0)
        : undefined;
    const sessionCap = isUnlimited
      ? globalCap
      : Math.max((remaining ?? 0) + currentQty, currentQty);
    const maxForSession =
      sessionCap != null && globalCap != null
        ? Math.min(sessionCap, globalCap)
        : (sessionCap ?? globalCap);
    const soldOut = !isUnlimited && (remaining ?? 0) <= 0 && currentQty === 0;
    const label = session.timeLabel
      ? `${formatDayLabel(session.dateISO, language)} • ${session.timeLabel}`
      : formatDayLabel(session.dateISO, language);

    return (
      <Flex key={session.sessionId} flexDirection="column" gap="100">
        <UpsellSelectionCard
          mainText={label}
          subText={`$${formatPrice(price)}`}
          quantityEnabled={!isSinglePick}
          quantity={currentQty}
          minQuantity={0}
          maxQuantity={maxForSession ?? undefined}
          checked={currentQty > 0}
          // Selecting another session swaps the pick, so only sold-out
          // sessions are unavailable.
          disabled={isSinglePick && soldOut}
          onCheckedChange={on => setSessionQuantity(session, on ? 1 : 0)}
          onQuantityChange={quantity => setSessionQuantity(session, quantity)}
        />
        {soldOut ? (
          <SimpleFlag text={t("SOLD_OUT")} visual="destructive" />
        ) : !isUnlimited &&
          shouldShowLastFew(remaining, availability?.totalCapacity) ? (
          <SimpleFlag
            text={t("LIMITED_AVAILABILITY", { count: remaining ?? 0 })}
            visual="warning"
          />
        ) : isToday(session.dateISO) ? (
          <SimpleFlag text={t("HAPPENING_TODAY")} visual="accent.blue" />
        ) : isTomorrow(session.dateISO) ? (
          <SimpleFlag text={t("HAPPENING_TOMORROW")} visual="accent.blue" />
        ) : null}
      </Flex>
    );
  };

  // Days that should show a stepper: the active day plus any day already in cart
  // (so the buyer can keep adjusting earlier selections).
  const cartDays = new Set(
    eventCartRows
      .map(item => item.sessionId?.split("::")[0])
      .filter((d): d is string => Boolean(d)),
  );
  const shownDays = Array.from(
    new Set([...(activeDay ? [activeDay] : []), ...cartDays]),
  ).sort();

  return (
    <CollapsableStep
      title={t("TITLE", { count: copyCount })}
      complete={selectedCount > 0}
      stepId="event-session"
    >
      <Text color="text.mid">{t("SUBTITLE", { count: copyCount })}</Text>
      {hasNoFutureSessions && (
        <SimpleFlag text={t("NO_FUTURE_SESSIONS")} visual="warning" />
      )}
      <Flex flexDirection="column" gap="200">
        {!useDayPicker ? (
          // Day-granularity, few days: a stepper card per day in place.
          days.flatMap(dateISO =>
            (sessionsByDate[dateISO] ?? []).map(renderSessionCard),
          )
        ) : (
          <>
            <EventDaySelector
              days={days}
              value={activeDay ? [activeDay] : []}
              onChange={next => setActiveDay(next[0] ?? "")}
              isDaySoldOut={isDaySoldOut}
              isToday={isToday}
              isTomorrow={isTomorrow}
              todayLabel={t("HAPPENING_TODAY")}
              tomorrowLabel={t("HAPPENING_TOMORROW")}
              language={language}
              placeholder={t("DATE_PLACEHOLDER")}
            />
            {shownDays.map(dateISO => (
              <Flex key={dateISO} flexDirection="column" gap="100">
                {hasTimeSlots && (
                  <Text color="text.dark" visual="smallMedium">
                    {formatDayLabel(dateISO, language)}
                  </Text>
                )}
                {(sessionsByDate[dateISO] ?? []).map(renderSessionCard)}
              </Flex>
            ))}
          </>
        )}
      </Flex>
    </CollapsableStep>
  );
};
